import identity from "ramda/es/identity";
import propOr from "ramda/es/propOr";
import { useEffect, useState } from 'react';
import { first, map } from 'rxjs/operators';
import { skipWhenStateEqual } from '../../../stream/utils';
import useContextOrStorage from '../../stream/useContextOrStorage';
/**
 * Hooks will provide form state. Always use memoized selector function !!.
 *
 * @documented
 * @category State
 * @import import useFormState from '@gef-ui/form-core/hooks/state/useFormState'
 * @param {Function} [selector=identity] selector which can take part of form state.
 * @param {String} [formName] - when used, formStream$ is taken from stream storage instead of context
 * @returns {Object} formState slice
 * @example
 *
 * 	const wholeFormState = useFormState();
 */ const useFormState = (selector = identity, formName)=>{
    const [data, setData] = useState(null);
    const { formState$ } = useContextOrStorage(formName);
    useEffect(()=>{
        if (formState$) {
            // one time subscription for initial data
            formState$.pipe(map(selector), first()).subscribe(setData);
            const sub = formState$.pipe(skipWhenStateEqual(), map(selector)).subscribe(setData);
            return ()=>{
                sub.unsubscribe();
            };
        }
    }, [
        formState$,
        selector
    ]);
    return data;
};
const propPristine = propOr(true, 'pristine');
/**
 * Hooks will provide pristine info about form.
 *
 * @documented
 * @category State
 * @import import { useIsFormPristine } from '@gef-ui/form-core/hooks/state/useFormState'
 * @param {String} [formName] - when used, formStream$ is taken from stream storage instead of context
 * @returns {Object} formState slice
 * @example
 *
 * 	const isFormPristine = useIsFormPristine();
 */ export const useIsFormPristine = (formName)=>useFormState(propPristine, formName);
export default useFormState;
